import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import { Button, Flex } from '@bridebook/aura';
import { Copy, Share, Check } from '@bridebook/aura/src/icons';
import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import { useCopyToClipboardWithFeedback } from '@bridebook/web/lib/utils/hooks/use-copy-to-clipboard-with-feedback';
import { PublicUrls } from '@bridebook/web/lib/url-helper';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { env } from '@bridebook/web/lib/env';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useDispatch } from '@bridebook/web/app-shared/lib/utils';
import { getGuestRsvpStatus } from '@bridebook/web/app-basic/lib/rsvp/utils';
import type { GetRsvpDataResponse } from '@bridebook/web/pages/api/for/website/[websiteId]/rsvp/guest/get';

interface IPartyMemberRowProps {
  member: GetRsvpDataResponse['relatedGuests'][number];
  websiteId: string;
  onStartRsvp: (memberName: string, memberId: string) => void;
}

export const PartyMemberRow: React.FC<IPartyMemberRowProps> = ({
  member,
  websiteId,
  onStartRsvp,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(getIsMobile);

  const { t } = useTranslation('weddingWebsite');
  const market = useMarket();
  const { isCopied, copy } = useCopyToClipboardWithFeedback();

  const canNativeShare = isMobile && typeof navigator !== 'undefined' && !!navigator?.share;
  const status = getGuestRsvpStatus(member.invitation);

  const memberRsvpUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : env.COUPLESIDE_URL;

    const rsvpPath = PublicUrls.for.rsvp(websiteId, { guest: member.name, guestId: member.id });
    return `${origin}/${market.prefix}${rsvpPath}`;
  }, [websiteId, member.name, member.id, market.prefix]);

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share({
        url: memberRsvpUrl,
        title: t('rsvp.guest.confirmation.shareLink.title'),
      });
    } catch {
      // Share was cancelled
    }
  }, [memberRsvpUrl, t]);

  const handleCopyLink = useCallback(() => {
    copy(memberRsvpUrl);
  }, [copy, memberRsvpUrl]);

  const trackPlusOnesClick = useCallback(
    (cta: string) => {
      dispatch(
        clickedOnLoggedOutPage({
          clickedLocation: 'rsvpGuestView',
          clickedSection: 'plusOnes',
          cta,
        }),
      );
    },
    [dispatch],
  );

  return (
    <Flex className="flex-row items-center justify-between gap-4">
      <div className="flex-1 text-sm font-semibold text-space">{member.name}</div>
      <Flex className="flex-row items-center gap-2">
        {status === 'responded' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              trackPlusOnesClick('Edit response');
              onStartRsvp(member.name, member.id);
            }}>
            {t('rsvp.guest.confirmation.cta.editResponse')}
          </Button>
        ) : (
          <>
            {canNativeShare ? (
              <Button
                icon={Share}
                variant="secondary"
                size="sm"
                disabled={status === 'noInvitation'}
                onClick={() => {
                  trackPlusOnesClick('share');
                  handleNativeShare();
                }}
              />
            ) : (
              <Button
                icon={isCopied ? Check : Copy}
                variant="secondary"
                size="sm"
                disabled={status === 'noInvitation'}
                onClick={() => {
                  trackPlusOnesClick('share');
                  handleCopyLink();
                }}
              />
            )}
            <Button
              size="sm"
              disabled={status === 'noInvitation'}
              onClick={() => {
                trackPlusOnesClick('Submit RSVP');
                onStartRsvp(member.name, member.id);
              }}>
              {t('rsvp.guest.confirmation.cta.submitRsvp')}
            </Button>
          </>
        )}
      </Flex>
    </Flex>
  );
};
